'use client';

import React from 'react';
import posthog from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import { useEffect } from 'react';
import { shouldDropExceptionEvent } from '@marketing-optimizer/ontology';
import type { SubmitIntent } from '@/components/modals/BookingModal.types';
import { captureAdAttribution } from '@/lib/ad-attribution';
import { percentageToTier, type ScoreTier } from '@/lib/design-system/scoring-colors';

/**
 * Session replay never records what a visitor types: every input is masked,
 * and email and phone fields are named as well, so they stay masked even if
 * `maskAllInputs` is ever relaxed (HAL-2022). A replay is matched to its lead
 * through the check id the checker events in the same session carry, not
 * through a typed address. Ad attribution does not come from replay either:
 * `captureAdAttribution` reads the landing URL's click IDs and UTMs into
 * storage, and the check carries them to the server.
 *
 * AI-NOTE: this replaced `maskInputOptions: { email: false }`, which tried to
 * unmask typed emails. The rrweb build posthog-js ships ignores
 * `maskInputOptions` while `maskAllInputs` is true, so emails were in practice
 * masked; `PostHogProvider.spec.tsx` replays typed values through that rrweb
 * build to keep it so.
 */
export const SESSION_RECORDING_MASKING = {
  maskAllInputs: true,
  maskInputOptions: { email: true, tel: true },
} as const;

/**
 * PostHog analytics provider for the landing app.
 *
 * Initializes PostHog with the project API key and configures:
 * - Automatic pageview capture
 * - Masked session replay, scoped to the public landing site in PostHog project settings
 * - API host pointing to PostHog Cloud
 *
 * Requires environment variable:
 * - NEXT_PUBLIC_POSTHOG_KEY: Your PostHog project API key
 */
export function PostHogProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  useEffect(() => {
    // First-touch ad click capture (HAL-1969). Runs on every full page load,
    // ahead of the PostHog key check so attribution never depends on analytics
    // being configured. See `@/lib/ad-attribution`.
    captureAdAttribution();

    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;

    if (!key) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          '[PostHog] NEXT_PUBLIC_POSTHOG_KEY not set. Analytics disabled.'
        );
      }
      return;
    }

    posthog.init(key, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
      person_profiles: 'identified_only',
      capture_pageview: true,
      capture_pageleave: true,
      // Drop known third-party / extension / vendor-script exception noise at
      // ingest (HAL-234). Shared predicate with the create/enhance snippet sites.
      before_send: (event) => (shouldDropExceptionEvent(event) ? null : event),
      // The project URL filter controls which visits are recorded.
      disable_session_recording: false,
      session_recording: SESSION_RECORDING_MASKING,
      // Respect Do Not Track browser setting
      respect_dnt: true,
      // Load async to not block page render
      loaded: (posthog) => {
        if (process.env.NODE_ENV === 'development') {
          // Enable debug mode in development
          posthog.debug();
        }
      },
    });

    // platform:'landing' tags the (clean, ours) Halcy marketing site so the
    // "Halcy-owned errors" view can isolate it from provider sites (HAL-234).
    posthog.register({ platform: 'landing' });
  }, []);

  return <PHProvider client={posthog}>{children}</PHProvider>;
}

/**
 * Start session recording for the current session.
 * Call this when users enter high-value flows (e.g., AI visibility checker).
 *
 * This is idempotent - calling it multiple times has no effect if already recording.
 */
export function startSessionRecording(): void {
  if (typeof window !== 'undefined') {
    posthog.startSessionRecording();
  }
}

/**
 * The score tier stamped on every checker event: the same `percentageToTier`
 * boundaries the report UI renders, so an event's `tier` matches what the
 * visitor saw.
 */
function getScoreTier(totalScore: number | null): ScoreTier | 'unavailable' {
  if (totalScore === null) return 'unavailable';
  return percentageToTier(totalScore);
}

/**
 * PostHog event tracking utilities for the AI Visibility Checker.
 *
 * All events follow the naming convention: `ai_visibility_checker_<action>`
 */
export const aiVisibilityCheckerEvents = {
  /**
   * Track when a user submits their email to unlock the report.
   */
  emailCaptured: (properties: {
    checkId: string;
    practiceName: string;
    totalScore: number | null;
    seoScore: number | null;
    aeoScore: number | null;
    mentionRate: number | null;
    /**
     * Page that produced the check this email unlocked, using the shared
     * discovery vocabulary (see `@/lib/discovery-source`). Must never be
     * hardcoded — it was pinned to the literal `'organic'` at its only call
     * site until HAL-726, which made the field a constant and useless for
     * attribution. Omit it when the originating page is genuinely unknown.
     */
    source?: string;
  }) => {
    posthog.capture('ai_visibility_checker_email_captured', {
      ...properties,
      tier: getScoreTier(properties.totalScore),
    });
  },

  /**
   * Track when a check is started.
   * Also starts session recording to capture the high-value checker flow.
   */
  checkStarted: (properties: {
    checkId: string;
    practiceName: string;
    hasWebsite: boolean;
    /**
     * Page that produced the check, using the shared discovery vocabulary
     * (see `@/lib/discovery-source`). Absent when the check was started from
     * a surface that does not carry attribution (direct link, hero input,
     * fast-report) — omitted rather than defaulted, so a missing source reads
     * as "unknown" instead of silently attributing to a real page.
     */
    source?: string;
  }) => {
    // Start session recording when check begins
    startSessionRecording();
    posthog.capture('ai_visibility_checker_check_started', properties);
  },

  /**
   * Track when a check is completed.
   */
  checkCompleted: (properties: {
    checkId: string;
    practiceName: string;
    totalScore: number | null;
    seoScore: number | null;
    aeoScore: number | null;
    mentionRate: number | null;
    queriesWithMentions: number;
    totalQueries: number;
  }) => {
    posthog.capture('ai_visibility_checker_check_completed', {
      ...properties,
      tier: getScoreTier(properties.totalScore),
    });
  },

  /**
   * The scan's timing (HAL-1998): when each piece of data reached the
   * browser, when each phase came on screen and whether its data was there,
   * and the reveal. Offsets are ms from the SSE connection opening, so the
   * phase windows in `timing-config.ts` can be retuned from real scans.
   * `variant: 'fast'` marks an admin (/fast-report) session: the same scan,
   * tagged so it can be left out of that tuning.
   */
  scanDataArrived: (properties: {
    checkId: string;
    event: string;
    offsetMs: number;
    variant: 'default' | 'fast';
    [key: string]: string | number | boolean;
  }): void => {
    posthog.capture('ai_visibility_checker_scan_data_arrived', properties);
  },

  scanPhaseStarted: (properties: {
    checkId: string;
    phase: string;
    offsetMs: number;
    dataPresent: boolean;
    variant: 'default' | 'fast';
  }): void => {
    posthog.capture('ai_visibility_checker_scan_phase_started', properties);
  },

  scanRevealed: (properties: {
    checkId: string;
    offsetMs: number;
    /** When the report became ready, so held time = offsetMs - readyOffsetMs. */
    readyOffsetMs: number | null;
    variant: 'default' | 'fast';
  }): void => {
    posthog.capture('ai_visibility_checker_scan_revealed', properties);
  },

  /**
   * Track when a user views the gated results (before unlocking).
   */
  gatedResultsViewed: (properties: {
    checkId: string;
    totalScore: number | null;
  }) => {
    posthog.capture('ai_visibility_checker_gated_results_viewed', {
      ...properties,
      tier: getScoreTier(properties.totalScore),
    });
  },

  /**
   * Track when a user clicks "Book a consult" CTA.
   */
  consultCtaClicked: (properties: {
    checkId?: string;
    totalScore?: number | null;
    location: 'sidebar' | 'results' | 'banner';
  }) => {
    posthog.capture('ai_visibility_checker_consult_cta_clicked', {
      ...properties,
      tier: properties.totalScore !== undefined ? getScoreTier(properties.totalScore) : undefined,
    });
  },

  /**
   * Track when the SSE stream drops or stalls and the client falls back to
   * polling `/api/tools/ai-visibility-checker/check/[checkId]` to recover.
   * Use this to measure how often the fault-tolerance path saves a session
   * that would otherwise have required a refresh (GEN-1513).
   *
   * `reason` – what triggered the fallback
   * `outcome` – whether we ultimately recovered and how
   */
  recoveryTriggered: (properties: {
    checkId: string;
    reason: 'sse_error' | 'watchdog_timeout' | 'inter_event_silence';
    outcome:
      | 'recovered_completed'
      | 'recovered_running'
      | 'recovered_failed'
      | 'failed_network'
      | 'failed_timeout';
    sseOpenDurationMs: number;
    recoveryAttempts: number;
    presetDurationMs: number;
  }) => {
    posthog.capture('ai_visibility_checker_recovery_triggered', properties);
  },
};

/**
 * PostHog event tracking for the global booking modal (HAL-728).
 *
 * Booking is the highest-intent action on the site and emitted nothing until
 * this existed – every CTA carrying `BOOKING_CTA_LABEL` funnels into
 * `/api/booking/capture-lead`, which writes a `leads` row server-side where no
 * PostHog person is visible. The capture has to happen on the client or the
 * booking cannot be tied to the session that produced it.
 */
export const bookingEvents = {
  /**
   * Fires once `/api/booking/capture-lead` confirms the lead.
   */
  submitted: (properties: {
    /** Path the modal was opened from (`window.location.pathname`). */
    pagePath: string;
    /** Always `request-only` now; kept so the event's history reads continuously. */
    intent: SubmitIntent;
    offer?: string;
    failureCode?: string;
    /**
     * Originating page in the shared discovery vocabulary (see
     * `@/lib/discovery-source`), plus `homepage` for the global surfaces.
     * Omitted – never defaulted – when the path maps to no known source, so a
     * gap reads as "unknown" rather than as a real page. Same contract as
     * `checkStarted`.
     */
    source?: string;
  }): void => {
    posthog.capture('booking_lead_submitted', properties);
  },

  /**
   * Fires once per open when the modal shows Nathan's calendar (HAL-2083).
   * The booking itself happens inside Google's cross-origin frame, so this is
   * the last step of the funnel the page can observe; bookings are counted
   * from Google's "Appointment booked" confirmation emails by the daily ads step.
   */
  calendarViewed: (properties: {
    pagePath: string;
    /** The control that opened the modal: a CTA location or a report surface. */
    placement: string;
    /**
     * `direct` when the modal opened on the calendar (the report's "Book a 15m
     * call"); `request-form` when the reader switched to it from the form or
     * the confirmation.
     */
    entry: 'direct' | 'request-form';
    /** Same vocabulary and omission rule as `submitted`. */
    source?: string;
  }): void => {
    posthog.capture('booking_calendar_viewed', properties);
  },
};

/**
 * PostHog event tracking utilities for the discovery report scanning UI.
 *
 * Pairs with the server-side `[map-fallback]` events (GEN-1657). Use when
 * the client observes a fallback condition that the server cannot see –
 * currently just iframe errors on `<GoogleMapsBackground>`.
 */
export const discoveryReportEvents = {
  /**
   * Fires when the Google Maps Embed iframe inside the competitor map
   * background fails to load. Properties capture the configuration at
   * render time so we can distinguish "no API key" from "API rejected
   * the embed".
   */
  mapIframeError: (properties: {
    checkId?: string;
    hadApiKey: boolean;
    hadCoordinates: boolean;
  }): void => {
    posthog.capture('map_iframe_error', properties);
  },
};

/**
 * PostHog event tracking utilities for hero section interactions.
 *
 * All events follow the naming convention: `hero_{action}`
 */
export const heroEvents = {
  /**
   * Track when a user focuses the hero input field.
   * Indicates intent to use the AI visibility checker.
   */
  inputFocused: (): void => {
    posthog.capture('hero_input_focused');
  },

  /**
   * Track when a user submits the hero input form.
   * This fires before the API call - use ai_visibility_checker_check_started for post-API tracking.
   *
   * AI-NOTE: The URL is intentionally captured for funnel analysis and lead attribution.
   * This follows the same pattern as email tracking in session recordings.
   */
  inputSubmitted: (properties: { url: string }): void => {
    posthog.capture('hero_input_submitted', properties);
  },

  /**
   * Track when a user clicks the "Book a call" secondary CTA in the hero.
   */
  bookingClicked: (): void => {
    posthog.capture('hero_booking_clicked');
  },
};

/**
 * PostHog event tracking utilities for general landing page interactions.
 *
 * All events follow the naming convention: `landing_{feature}_{action}`
 * except for learn pages which use `learn_page_viewed`.
 */
export const landingPageEvents = {
  /**
   * Track scroll depth milestones (25%, 50%, 75%, 100%).
   * Each milestone should only fire once per session.
   */
  scrollMilestone: (depth: 25 | 50 | 75 | 100) => {
    posthog.capture('landing_scroll_depth', { depth });
  },

  /**
   * Track when a user expands an FAQ accordion item.
   */
  faqExpanded: (questionId: string) => {
    posthog.capture('landing_faq_expanded', { question_id: questionId });
  },

  /**
   * Track CTA button clicks with location context.
   */
  ctaClicked: (ctaId: string, location: string) => {
    posthog.capture('landing_cta_clicked', { cta_id: ctaId, location });
  },

  /**
   * Track learn page views with slug and category context.
   *
   * Uses `learn_page_viewed` (not `landing_learn_page_viewed`) because
   * learn pages are a distinct content type with their own URL path (/learn/),
   * not a section of the landing page.
   */
  learnPageViewed: (slug: string, category: string) => {
    posthog.capture('learn_page_viewed', { slug, category });
  },

  /**
   * Track navigation link clicks.
   */
  navLinkClicked: (link: string, section: string) => {
    posthog.capture('landing_nav_clicked', { link, section });
  },
};

/**
 * PostHog event tracking for discovery CTA components on /learn and /for pages.
 *
 * Source attribution enables comparing conversion rates across page types.
 * All events follow the naming convention: `discovery_cta_<action>`
 */
export const discoveryCTAEvents = {
  /** Track when a user focuses the inline visibility checker input. */
  inputFocused: (source: string) => {
    posthog.capture('discovery_cta_input_focused', { source });
  },

  /** Track when a user submits the inline visibility checker form. */
  inputSubmitted: (properties: { source: string; url: string }) => {
    posthog.capture('discovery_cta_input_submitted', properties);
  },

  /** Track when a user clicks the mid-article callout CTA. */
  midArticleClicked: (source: string) => {
    posthog.capture('discovery_cta_mid_article_clicked', { source });
  },

  /** Track when the sticky scroll bar becomes visible. */
  stickyBarShown: (source: string) => {
    posthog.capture('discovery_cta_sticky_bar_shown', { source });
  },

  /** Track when a user clicks the sticky scroll bar CTA. */
  stickyBarClicked: (source: string) => {
    posthog.capture('discovery_cta_sticky_bar_clicked', { source });
  },

  /** Track when a user dismisses the sticky scroll bar. */
  stickyBarDismissed: (source: string) => {
    posthog.capture('discovery_cta_sticky_bar_dismissed', { source });
  },
};

/** Offer exposure and intent, distinct from a saved booking request. No contact details. */
export const bookingOfferEvents = {
  viewed: (properties: { offer: string; placement: string; pagePath: string; failureCode?: string }): void => {
    posthog.capture('booking_offer_viewed', properties);
  },
  clicked: (properties: { offer: string; placement: string; pagePath: string; failureCode?: string }): void => {
    posthog.capture('booking_offer_clicked', properties);
  },
};
